import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';

export const notificationProvidersEndpoint = 'api/v1/cms/notification-providers';

export type PhoneOtpProvider = 'twilio_sms' | 'cequens_sms' | 'twilio_whatsapp';
export type EmailProvider = 'resend' | 'sendgrid';

/** `cms`: saved on this screen; `env`: the server setting applies. */
export type SettingSource = 'cms' | 'env';

export interface ProviderAvailability<T> {
  id: T;
  /** The server has the keys this provider needs. */
  configured: boolean;
}

interface ChangeInfo {
  updatedAt: string | null;
  updatedBy: { id: number; name: string | null } | null;
}

export interface NotificationProviderSettings {
  phoneOtp: ChangeInfo & {
    providers: PhoneOtpProvider[];
    source: SettingSource;
    envProviders: PhoneOtpProvider[];
    available: ProviderAvailability<PhoneOtpProvider>[];
  };
  email: ChangeInfo & {
    provider: EmailProvider;
    source: SettingSource;
    envProvider: EmailProvider;
    available: ProviderAvailability<EmailProvider>[];
  };
}

type TData = NotificationProviderSettings;
type TError = AxiosError;
type TQueryKey = [typeof notificationProvidersEndpoint];
type Options = Omit<UseQueryOptions<TData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetNotificationProvidersApi(options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();

  const queryFn = async () => {
    const { data } = await http.get(notificationProvidersEndpoint);
    return data.data as NotificationProviderSettings;
  };

  return useQueryWrapper([notificationProvidersEndpoint], queryFn, { enabled: isLoggedIn, ...options });
}
